// The road still to drive, and how long it takes — as the map and the
// day bar draw them.
//
// BRAND.md spends the identity on one figure: a muted track with a
// shorter amber range inside it. This module produces both halves for
// every round that is out:
//
//   **range**  the leg being driven *now*: the vehicle to its next door.
//   **track**  everything after that, door to door, to the end of the round.
//
// Both come back from `engine.route()`, the same call that produced the
// arrival time the console already shows. That matters more than it
// looks: the alternative — joining the doors with straight lines — draws
// a road that does not exist, and this surface's rule (DESIGN: *maps
// that only draw what is true*) is that it may not. If the router cannot
// answer, the caller gets nothing and draws nothing.
//
// `secondsRemaining` comes off the same legs. It is **driving time only**
// — this module has no idea how long a courier spends at a door, and
// inventing a dwell allowance would turn a measured number into a guess
// wearing its clothes. Callers must say "driving" when they show it.
//
// The gate throughout is the one the pin obeys: a run contributes a road
// only when it has a **live** position, so a coarse or stale round gets
// no route and no projection rather than a plausible one.

import { useEffect, useState } from "react";
import type { LatLon, RouteEngine } from "@wayfind/core";

export interface RemainingRoad {
  /** Everything left to drive, including the current leg. */
  track: [number, number][];
  /** The current leg alone — vehicle to next door. */
  range: [number, number][];
  /**
   * Driving seconds from the vehicle to the last door this module
   * routed. Null when nothing could be routed. Never includes time
   * spent at a door.
   */
  secondsRemaining: number | null;
  /**
   * True when the round has more doors than `MAX_LEGS`, so the figures
   * above describe a prefix of the round rather than all of it.
   */
  truncated: boolean;
  /**
   * Which way the vehicle is pointing, in degrees clockwise from north,
   * taken from the direction its own leg leaves from. The run does not
   * publish a heading, so this is the road's bearing at the vehicle
   * rather than a compass reading, and it is null when there is no leg.
   */
  headingDegrees: number | null;
}

export const NO_ROAD: RemainingRoad = {
  track: [], range: [], secondsRemaining: null, truncated: false, headingDegrees: null
};

/** One round's ask: where its van is, and which doors it has left. */
export interface RoadRequest {
  id: string;
  from: LatLon | null;
  doors: LatLon[];
}

/**
 * How many legs ahead to route, per round.
 *
 * Each is a separate `route()` call. A fifty-drop round would spend the
 * engine on road nobody is looking at, and the shape of the rest of the
 * day is legible well before then — so the track stops here and says so
 * through `truncated` rather than pretending it reached the last door.
 */
const MAX_LEGS = 12;

/**
 * How many rounds to route at once.
 *
 * A depot with forty vans out would otherwise fire hundreds of routing
 * calls on every fix. Callers order the list by whatever the board is
 * ranking on, so the rounds that lose their road are the ones already
 * sorted to the bottom.
 */
const MAX_RUNS = 10;

/** Bearing from one point to the next, clockwise from north. */
function bearing(from: [number, number], to: [number, number]): number | null {
  const dLat = to[0] - from[0];
  // Longitude degrees shrink with latitude; without the cosine a van on
  // an east-west street points visibly wrong this far north.
  const dLon = (to[1] - from[1]) * Math.cos((from[0] * Math.PI) / 180);
  if (dLat === 0 && dLon === 0) return null;
  return ((Math.atan2(dLon, dLat) * 180) / Math.PI + 360) % 360;
}

async function roadFor(
  engine: RouteEngine,
  from: LatLon,
  doors: LatLon[]
): Promise<RemainingRoad> {
  const legs: [number, number][][] = [];
  let seconds = 0;
  let cursor: LatLon = from;
  for (const door of doors.slice(0, MAX_LEGS)) {
    const leg = await engine.route({ from: cursor, to: door, geometry: true }).catch(() => null);
    // One unroutable leg should not blank the whole road — the legs
    // before it are still true, and a partial road is honest so long as
    // nothing claims it reaches the end.
    if (!leg?.geometry?.length) break;
    legs.push(leg.geometry);
    seconds += leg.seconds;
    cursor = door;
  }
  if (!legs.length) return NO_ROAD;
  const range = legs[0]!;
  return {
    track: legs.flat(),
    range,
    secondsRemaining: seconds,
    truncated: doors.length > MAX_LEGS || legs.length < Math.min(doors.length, MAX_LEGS),
    headingDegrees: range.length > 1 ? bearing(range[0]!, range[1]!) : null
  };
}

/**
 * Route every round that has somewhere to be, keyed by run id.
 *
 * `requests` must already be in board order with settled doors removed —
 * this module does not decide what counts as done, it only draws what it
 * is handed.
 */
export function useRemainingRoads(
  engine: RouteEngine | null,
  requests: RoadRequest[]
): Record<string, RemainingRoad> {
  const [roads, setRoads] = useState<Record<string, RemainingRoad>>({});

  // Rounded to about a metre. A parked van jitters by centimetres between
  // fixes, and re-routing the board on that jitter would be a lot of
  // engine for a picture that did not change.
  const key = requests
    .map(request => [
      request.id,
      request.from ? `${request.from.lat.toFixed(5)},${request.from.lon.toFixed(5)}` : "-",
      request.doors.map(door => `${door.lat.toFixed(5)},${door.lon.toFixed(5)}`).join(";")
    ].join(":"))
    .join("|");

  useEffect(() => {
    if (!engine) { setRoads({}); return; }
    const live = { on: true };
    const wanted = requests
      .filter(request => request.from && request.doors.length)
      .slice(0, MAX_RUNS);
    if (!wanted.length) { setRoads({}); return; }

    void (async () => {
      const next: Record<string, RemainingRoad> = {};
      for (const request of wanted) {
        const road = await roadFor(engine, request.from!, request.doors);
        if (!live.on) return;
        next[request.id] = road;
        // Publish as each round finishes rather than at the end, so the
        // first van draws while the tenth is still routing.
        setRoads({ ...next });
      }
    })();

    return () => { live.on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, key]);

  return roads;
}

/** The single-round case, for screens that only ever show one. */
export function useRemainingRoad(
  engine: RouteEngine | null,
  from: LatLon | null,
  doors: LatLon[]
): RemainingRoad {
  const roads = useRemainingRoads(engine, [{ id: "one", from, doors }]);
  return roads.one ?? NO_ROAD;
}
